/**
 * FAQ Section Component
 *
 * React implementation of the FAQ section renderer.
 * Supports variants: accordion, grid, tabs
 */

import { useState } from 'react';
import { FaqData, SectionProps, EditableElement as EditableElementType } from '../types';
import { EditableText } from '../components/EditableElement';

// =============================================================================
// TYPES
// =============================================================================

interface FaqProps extends SectionProps<FaqData> {}

// =============================================================================
// HELPER: Find element by path
// =============================================================================

function findElement(elements: EditableElementType[] | undefined, path: string): EditableElementType | undefined {
  return elements?.find(el => el.path === path);
}

// =============================================================================
// MAIN COMPONENT
// =============================================================================

export function Faq({ data, ctx, sectionId, elements }: FaqProps) {
  const variant = data.variant || 'accordion';

  switch (variant) {
    case 'grid':
      return <FaqGrid data={data} ctx={ctx} sectionId={sectionId} elements={elements} />;
    case 'tabs':
      return <FaqTabs data={data} ctx={ctx} sectionId={sectionId} elements={elements} />;
    default:
      return <FaqAccordion data={data} ctx={ctx} sectionId={sectionId} elements={elements} />;
  }
}

// =============================================================================
// ACCORDION VARIANT
// =============================================================================

function FaqAccordion({ data, ctx, elements }: FaqProps) {
  const { recipe } = ctx;
  const [openIndex, setOpenIndex] = useState<number | null>(0);

  const eyebrowEl = findElement(elements, 'eyebrow');
  const headlineEl = findElement(elements, 'headline');
  const subheadlineEl = findElement(elements, 'subheadline');

  const toggleFaq = (index: number) => {
    setOpenIndex(openIndex === index ? null : index);
  };

  return (
    <section
      id="faq"
      className="py-20 lg:py-28"
      style={{
        backgroundColor: recipe.colors.background,
        fontFamily: `${recipe.typography.bodyFont}, sans-serif`,
      }}
    >
      <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="text-center mb-12">
          {(data.eyebrow || eyebrowEl) && (
            <div className="mb-4">
              {eyebrowEl ? (
                <EditableText
                  element={eyebrowEl}
                  as="span"
                  className="text-sm font-semibold uppercase tracking-wider"
                  style={{ color: recipe.colors.primary }}
                />
              ) : (
                <span
                  className="text-sm font-semibold uppercase tracking-wider"
                  style={{ color: recipe.colors.primary }}
                >
                  {data.eyebrow}
                </span>
              )}
            </div>
          )}

          {headlineEl ? (
            <EditableText
              element={headlineEl}
              as="h2"
              className="text-3xl md:text-4xl font-bold mb-4"
              style={{
                color: recipe.colors.text,
                fontFamily: `${recipe.typography.headingFont}, sans-serif`,
              }}
            />
          ) : data.headline ? (
            <h2
              className="text-3xl md:text-4xl font-bold mb-4"
              style={{
                color: recipe.colors.text,
                fontFamily: `${recipe.typography.headingFont}, sans-serif`,
              }}
            >
              {data.headline}
            </h2>
          ) : null}

          {subheadlineEl ? (
            <EditableText
              element={subheadlineEl}
              as="p"
              className="text-lg"
              style={{ color: recipe.colors.textMuted }}
            />
          ) : data.subheadline ? (
            <p className="text-lg" style={{ color: recipe.colors.textMuted }}>
              {data.subheadline}
            </p>
          ) : null}
        </div>

        {/* FAQ Accordion */}
        <div className="space-y-4">
          {data.faqs?.map((faq, i) => {
            const questionEl = findElement(elements, `faqs.${i}.question`);
            const answerEl = findElement(elements, `faqs.${i}.answer`);
            const isOpen = openIndex === i;

            return (
              <div
                key={i}
                className="rounded-xl overflow-hidden"
                style={{
                  backgroundColor: recipe.colors.surface,
                  borderRadius: recipe.borders.cardRadius,
                }}
              >
                <button
                  onClick={() => toggleFaq(i)}
                  className="w-full flex items-center justify-between p-5 text-left"
                >
                  {questionEl ? (
                    <EditableText
                      element={questionEl}
                      as="span"
                      className="font-semibold pr-4"
                      style={{ color: recipe.colors.text }}
                    />
                  ) : (
                    <span className="font-semibold pr-4" style={{ color: recipe.colors.text }}>
                      {faq.question}
                    </span>
                  )}
                  <svg
                    className={`w-5 h-5 flex-shrink-0 transition-transform ${isOpen ? 'rotate-180' : ''}`}
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                    style={{ color: recipe.colors.primary }}
                  >
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                  </svg>
                </button>
                {isOpen && (
                  <div className="px-5 pb-5">
                    {answerEl ? (
                      <EditableText
                        element={answerEl}
                        as="p"
                        style={{ color: recipe.colors.textMuted }}
                      />
                    ) : (
                      <p style={{ color: recipe.colors.textMuted }}>{faq.answer}</p>
                    )}
                  </div>
                )}
              </div>
            );
          })}
        </div>

        {/* CTA */}
        {data.cta && (
          <div
            className="mt-12 text-center p-6 rounded-xl"
            style={{ backgroundColor: recipe.colors.surface }}
          >
            <p className="mb-4" style={{ color: recipe.colors.textMuted }}>
              Still have questions?
            </p>
            <div className="flex flex-wrap justify-center gap-4">
              {data.cta.url && (
                <a
                  href={data.cta.url}
                  className="inline-flex items-center px-6 py-3 font-semibold rounded-lg text-white"
                  style={{
                    backgroundColor: recipe.colors.primary,
                    borderRadius: recipe.borders.buttonRadius,
                  }}
                >
                  {data.cta.text}
                </a>
              )}
              {data.cta.phone && (
                <a
                  href={`tel:${data.cta.phone}`}
                  className="inline-flex items-center gap-2 px-6 py-3 font-semibold rounded-lg border"
                  style={{
                    color: recipe.colors.primary,
                    borderColor: recipe.colors.primary,
                    borderRadius: recipe.borders.buttonRadius,
                  }}
                >
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z" />
                  </svg>
                  {data.cta.phone}
                </a>
              )}
            </div>
          </div>
        )}
      </div>
    </section>
  );
}

// =============================================================================
// GRID VARIANT
// =============================================================================

function FaqGrid({ data, ctx, elements }: FaqProps) {
  const { recipe } = ctx;

  const eyebrowEl = findElement(elements, 'eyebrow');
  const headlineEl = findElement(elements, 'headline');
  const subheadlineEl = findElement(elements, 'subheadline');

  return (
    <section
      id="faq"
      className="py-20 lg:py-28"
      style={{
        backgroundColor: recipe.colors.surface,
        fontFamily: `${recipe.typography.bodyFont}, sans-serif`,
      }}
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="text-center mb-12">
          {(data.eyebrow || eyebrowEl) && (
            <div className="mb-4">
              {eyebrowEl ? (
                <EditableText
                  element={eyebrowEl}
                  as="span"
                  className="text-sm font-semibold uppercase tracking-wider"
                  style={{ color: recipe.colors.primary }}
                />
              ) : (
                <span
                  className="text-sm font-semibold uppercase tracking-wider"
                  style={{ color: recipe.colors.primary }}
                >
                  {data.eyebrow}
                </span>
              )}
            </div>
          )}

          {headlineEl ? (
            <EditableText
              element={headlineEl}
              as="h2"
              className="text-3xl md:text-4xl font-bold mb-4"
              style={{
                color: recipe.colors.text,
                fontFamily: `${recipe.typography.headingFont}, sans-serif`,
              }}
            />
          ) : data.headline ? (
            <h2
              className="text-3xl md:text-4xl font-bold mb-4"
              style={{
                color: recipe.colors.text,
                fontFamily: `${recipe.typography.headingFont}, sans-serif`,
              }}
            >
              {data.headline}
            </h2>
          ) : null}

          {subheadlineEl ? (
            <EditableText
              element={subheadlineEl}
              as="p"
              className="text-lg max-w-2xl mx-auto"
              style={{ color: recipe.colors.textMuted }}
            />
          ) : data.subheadline ? (
            <p className="text-lg max-w-2xl mx-auto" style={{ color: recipe.colors.textMuted }}>
              {data.subheadline}
            </p>
          ) : null}
        </div>

        {/* FAQ Grid */}
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
          {data.faqs?.map((faq, i) => {
            const questionEl = findElement(elements, `faqs.${i}.question`);
            const answerEl = findElement(elements, `faqs.${i}.answer`);

            return (
              <div
                key={i}
                className="p-6 rounded-xl"
                style={{
                  backgroundColor: recipe.colors.background,
                  borderRadius: recipe.borders.cardRadius,
                  boxShadow: recipe.shadows.card,
                }}
              >
                {questionEl ? (
                  <EditableText
                    element={questionEl}
                    as="h3"
                    className="font-semibold mb-3"
                    style={{ color: recipe.colors.text }}
                  />
                ) : (
                  <h3 className="font-semibold mb-3" style={{ color: recipe.colors.text }}>
                    {faq.question}
                  </h3>
                )}

                {answerEl ? (
                  <EditableText
                    element={answerEl}
                    as="p"
                    className="text-sm"
                    style={{ color: recipe.colors.textMuted }}
                  />
                ) : (
                  <p className="text-sm" style={{ color: recipe.colors.textMuted }}>
                    {faq.answer}
                  </p>
                )}
              </div>
            );
          })}
        </div>
      </div>
    </section>
  );
}

// =============================================================================
// TABS VARIANT
// =============================================================================

function FaqTabs({ data, ctx, elements }: FaqProps) {
  const { recipe } = ctx;
  const categories = data.categories || [];
  const [activeCategory, setActiveCategory] = useState(categories[0]?.slug || '');

  const eyebrowEl = findElement(elements, 'eyebrow');
  const headlineEl = findElement(elements, 'headline');

  // Filter FAQs by category
  const filteredFaqs = activeCategory
    ? data.faqs?.filter(f => f.category === activeCategory)
    : data.faqs;

  return (
    <section
      id="faq"
      className="py-20 lg:py-28"
      style={{
        backgroundColor: recipe.colors.background,
        fontFamily: `${recipe.typography.bodyFont}, sans-serif`,
      }}
    >
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="text-center mb-12">
          {(data.eyebrow || eyebrowEl) && (
            <div className="mb-4">
              {eyebrowEl ? (
                <EditableText
                  element={eyebrowEl}
                  as="span"
                  className="text-sm font-semibold uppercase tracking-wider"
                  style={{ color: recipe.colors.primary }}
                />
              ) : (
                <span
                  className="text-sm font-semibold uppercase tracking-wider"
                  style={{ color: recipe.colors.primary }}
                >
                  {data.eyebrow}
                </span>
              )}
            </div>
          )}

          {headlineEl ? (
            <EditableText
              element={headlineEl}
              as="h2"
              className="text-3xl md:text-4xl font-bold"
              style={{
                color: recipe.colors.text,
                fontFamily: `${recipe.typography.headingFont}, sans-serif`,
              }}
            />
          ) : data.headline ? (
            <h2
              className="text-3xl md:text-4xl font-bold"
              style={{
                color: recipe.colors.text,
                fontFamily: `${recipe.typography.headingFont}, sans-serif`,
              }}
            >
              {data.headline}
            </h2>
          ) : null}
        </div>

        {/* Category Tabs */}
        {categories.length > 0 && (
          <div className="flex flex-wrap justify-center gap-2 mb-8">
            {categories.map((category) => (
              <button
                key={category.slug}
                onClick={() => setActiveCategory(category.slug)}
                className={`px-4 py-2 rounded-lg font-medium transition-colors ${
                  activeCategory === category.slug
                    ? 'text-white'
                    : ''
                }`}
                style={{
                  backgroundColor: activeCategory === category.slug
                    ? recipe.colors.primary
                    : recipe.colors.surface,
                  color: activeCategory === category.slug
                    ? 'white'
                    : recipe.colors.text,
                  borderRadius: recipe.borders.buttonRadius,
                }}
              >
                {category.name}
              </button>
            ))}
          </div>
        )}

        {/* FAQ List */}
        <div className="space-y-4">
          {filteredFaqs?.map((faq, i) => {
            const questionEl = findElement(elements, `faqs.${i}.question`);
            const answerEl = findElement(elements, `faqs.${i}.answer`);

            return (
              <div
                key={i}
                className="p-6 rounded-xl"
                style={{
                  backgroundColor: recipe.colors.surface,
                  borderRadius: recipe.borders.cardRadius,
                }}
              >
                {questionEl ? (
                  <EditableText
                    element={questionEl}
                    as="h3"
                    className="font-semibold mb-2"
                    style={{ color: recipe.colors.text }}
                  />
                ) : (
                  <h3 className="font-semibold mb-2" style={{ color: recipe.colors.text }}>
                    {faq.question}
                  </h3>
                )}

                {answerEl ? (
                  <EditableText
                    element={answerEl}
                    as="p"
                    style={{ color: recipe.colors.textMuted }}
                  />
                ) : (
                  <p style={{ color: recipe.colors.textMuted }}>{faq.answer}</p>
                )}
              </div>
            );
          })}
        </div>
      </div>
    </section>
  );
}

export default Faq;
